import { isEmpty } from 'lodash/fp';
import { useMemo } from 'react';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import { LinkifyExternal } from 'dashboard/components/LinkifyExternal';
import { MonoSpan } from 'dashboard/components/Typography';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { Case } from 'dashboard/models/case';
import {
  KeyValueData,
  KeyValueModuleDisplayFormat,
  ModuleKeyValue,
} from 'dashboard/modules/KeyValue/model';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { FieldJson } from 'lib/fields';

const EmptyStateContainer = styled('div')`
  display: flex;
  align-items: center;
  text-align: center;
`;

interface KeyValueTableProps {
  keyValuePairs: KeyValueData;
  keyLabel: string;
  valueLabel: string;
}

const KeyValueTable = ({ keyValuePairs, keyLabel, valueLabel }: KeyValueTableProps) => {
  return (
    <TableLayout
      headerTR={
        <>
          <TableTH>{keyLabel}</TableTH>
          <TableTH>{valueLabel}</TableTH>
        </>
      }
      nullState={<TableNullState dataTestBlock='key-value-table_null-state' />}
    >
      {Object.keys(keyValuePairs).map((key, index) => (
        <TableTR key={index}>
          <TableTD>{key}</TableTD>
          <TableTD>
            <LinkifyExternal>{keyValuePairs[key]}</LinkifyExternal>
          </TableTD>
        </TableTR>
      ))}
    </TableLayout>
  );
};

interface KeyValueAttributeGridProps {
  keyValuePairs: KeyValueData;
}

const KeyValueAttributeGrid = ({ keyValuePairs }: KeyValueAttributeGridProps) => {
  return (
    <AttributeGrid>
      {Object.keys(keyValuePairs).map((key, index) => (
        <AttributeItem
          title={key}
          value={<LinkifyExternal>{keyValuePairs[key]}</LinkifyExternal>}
          key={index}
        />
      ))}
    </AttributeGrid>
  );
};

export const stringifyObjectValues = (json: Record<string, any>): KeyValueData => {
  return Object.keys(json).reduce((prev, k) => {
    return {
      ...prev,
      [k]:
        typeof json[k] === 'string' // calling JSON.stringify on strings results in duplicate quotes
          ? json[k]
          : JSON.stringify(json[k]),
    };
  }, {});
};

const getKeyValuePairs = (kase: Case, fieldKey: string): KeyValueData => {
  if (
    isEmpty(kase.attributes.fields) ||
    !kase.attributes.fields[fieldKey] ||
    kase.attributes.fields[fieldKey].type !== 'json'
  ) {
    return {};
  }
  const field = kase.attributes.fields[fieldKey] as FieldJson;
  const data = field.value as Record<string, string>;
  const keyValues = isEmpty(data)
    ? {}
    : Object.keys(data).reduce((prev, k) => {
        return {
          ...prev,
          [k]: typeof data[k] === 'string' ? data[k] : JSON.stringify(data[k]),
        };
      }, {});
  return keyValues;
};

interface KeyValueProps {
  module: ModuleKeyValue;
  case: Case;
  isPreview: boolean;
}

const KeyValue = ({ module, case: kase, isPreview }: KeyValueProps) => {
  const keyValuePairsToDisplay = useMemo(() => {
    return isPreview ? module['preview-data'] : getKeyValuePairs(kase, module['field-key']);
  }, [module, kase]);

  return (
    <Card data-test={`key-value-${module.token}`}>
      <Card.Body>
        <Card.Title>
          {module.title}
          <HelpIcon>
            Field displayed: <MonoSpan>{module['field-key']}</MonoSpan>
          </HelpIcon>
        </Card.Title>
        <Spacer height={1.5} />
        {Object.keys(keyValuePairsToDisplay).length === 0 ? (
          <EmptyStateContainer data-test={`key-value-${module.token}_null-state`}>
            <Paragraph>
              Looks like there isn&#39;t any data for{' '}
              <MonoSpan>{module['field-key'] ? `${module['field-key']}` : 'this field'}</MonoSpan>{' '}
              on this case yet.
            </Paragraph>
          </EmptyStateContainer>
        ) : module['display-format'] === KeyValueModuleDisplayFormat.Grid ? (
          <KeyValueAttributeGrid keyValuePairs={keyValuePairsToDisplay} />
        ) : (
          <KeyValueTable
            keyValuePairs={keyValuePairsToDisplay}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            keyLabel={module['key-label']}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            valueLabel={module['value-label']}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default KeyValue;
